<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import EntityApi from '../sdk/EntityApi';
import Registration from '../sdk/models/Registration';
import Product from '../sdk/models/Product';
import { Pie } from 'vue-chartjs';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';

Chart.register(ArcElement, Tooltip, Legend);

const router = useRouter();

const routeTo = function(route:string) {
  router.push(route);
}

// Dashboard data
const stats = reactive({
  total: 0,
  thisYear: 0,
  angemeldetFuer: {} as Record<string, number>
});
const registrations = ref<Registration[]>([]);
const products = ref<Product[]>([]);

const fetchStats = async () => {
  const registrationApi = new EntityApi('registrations');
  const productApi = new EntityApi('products');
  registrations.value = await registrationApi.retrieveAll();
  products.value = await productApi.retrieveAll();

  const now = new Date();
  stats.total = registrations.value.length;
  stats.thisYear = registrations.value.filter(r => {
    const date = new Date(r.Timestamp!);
    return date.getFullYear() === now.getFullYear();
  }).length;

  // Pie chart data: count by resolved product name
  stats.angemeldetFuer = {};
  registrations.value.forEach(r => {
    const name = resolveProductName(r.PartitionKey);
    stats.angemeldetFuer[name] = (stats.angemeldetFuer[name] || 0) + 1;
  });
};

function resolveProductName(combinedProductName: string|null): string {
  if (!combinedProductName) return "Unbekannt";
  const splitted = combinedProductName.split('^');
  if (splitted.length > 1) {
    const productCode = splitted[1];
    const product = products.value.find(product => product.RowKey === productCode);
    return product?.Name ?? "Produkt nicht gefunden";
  }
  return "Produkt nicht gefunden";
}

onMounted(fetchStats);

// Pie chart config
const pieData = computed(() => {
  // Get entries and sort descending by count
  const sorted = Object.entries(stats.angemeldetFuer)
    .sort((a, b) => b[1] - a[1]);
  return {
    labels: sorted.map(([label]) => label),
    datasets: [{
      data: sorted.map(([, value]) => value),
      backgroundColor: [
        '#8BC34A', '#FFC107', '#03A9F4', '#E91E63', '#FF5722', '#9C27B0', '#607D8B'
      ]
    }]
  };
});
const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right' }
  },
  layout: {
    padding: 0
  }
} as any;
</script>

<template>
  <div class="form-container">
    <h3 style="margin-bottom:2rem">Dashboard</h3>
    <div class="dashboard-row">
      <div class="dashboard-stat">
        <div class="stat-label stat-label-top">Registrationen gesamt</div>
        <div class="stat-value">{{ stats.total }}</div>
      </div>
      <div class="dashboard-stat">
        <div class="stat-label stat-label-top">Registrationen dieses Jahr</div>
        <div class="stat-value">{{ stats.thisYear }}</div>
      </div>
      <div class="dashboard-chart">
        <Pie :data="pieData" :options="pieOptions" :height="220" :width="400" />
        <div class="stat-label" style="text-align:center;">Angemeldet Für</div>
      </div>
    </div>

    <h3 style="margin-bottom:2rem">Administration</h3>

    <div class="row">
      <div class="col-xs-4">
          <div class="box-row" @click="routeTo('/admin/registrations/')">
            <div class="card cardshadow">
              <h4>Registrationen</h4>
            </div>
          </div>
      </div>
      <div class="col-xs-4" @click="routeTo('/admin/products/')">
            <div class="box-row card cardshadow">
              <h4>Produkte</h4>
            </div>
      </div>
      
      <div class="col-xs-4">
          <div class="box-row card cardshadow" @click="routeTo('/admin/assets/')">
            <h4>Assets</h4>
          </div>
      </div>

      <div class="col-xs-4">
          <div class="box-row card cardshadow" @click="routeTo('/admin/courseitems/')">
            <h4>Kurs-Inhalte</h4>
          </div>
      </div>

    </div>

    <h3 style="margin-bottom:2rem">Berechtigungen</h3>

    <div class="row">
      
      <div class="col-xs-4" @click="routeTo('/admin/logins/')">
            <div class="box-row card cardshadow">
              <h4>Benutzer</h4>
            </div>
      </div>

      <div class="col-xs-4" @click="routeTo('/admin/groups/')">
            <div class="box-row card cardshadow">
              <h4>Gruppen</h4>
            </div>
      </div>
    </div>

    <h3 style="margin-bottom:2rem">Einstellungen</h3>

    <div class="row">
      <div class="col-xs-4">
          <div class="box-row card cardshadow" @click="routeTo('/admin/emailtemplates/')">
            <h4>Email-Templates</h4>
          </div>
      </div>
      
      <div class="col-xs-4">
          <div class="box-row card cardshadow" @click="routeTo('/admin/processes/')">
            <h4>Prozesse</h4>
          </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.card {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  height: 12rem;
  cursor:pointer;
}

div[class^="col-"] {
    margin-bottom: 2rem;
}

.card:hover {
  background-color: honeydew;
}

.dashboard-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.dashboard-stat {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 2rem 3rem;
  min-width: 180px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px; /* Match the pie chart height */
  position: relative; /* Needed for absolute positioning of label */
}

.dashboard-stat:last-child {
  margin-right: 0;
}

.stat-label {
  color: #888;
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
}

.stat-label-top {
  position: absolute;
  top: 2rem;
  left: 0;
  width: 100%;
  text-align: center;
  color: #888;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  pointer-events: none;
  z-index: 1;
}

.stat-value {
  font-size: 3.5rem;
  font-weight: bold;
  color: var(--main-color);
  line-height: 1;
  margin: 0;
}

.dashboard-chart {
  width: 400px;
  max-width: 100%;
  height: 300px; /* Same as .dashboard-stat */
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 1rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-chart .stat-label {
  position: absolute;
  bottom: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.875rem;
  color: #666;
}
</style>
